import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User, Organization, Membership, AuthResponse, SupportGrant } from '../types';
import { ApiError, recoverPlatformSession, tokens } from '../api/client';
import { authApi, type RegisterBody } from '../api/endpoints';
import { queryClient } from '../api/queryClient';
import { supportGrantKeys } from '../api/hooks';
import { AuthContext } from './authContext';
import { SupportContext, type SupportNotice } from './supportContext';
import { clearSessionState } from './clearSession';
import {
  announceScopeChange,
  supportSession,
  toSupportInfo,
  SCOPE_CHANGED_KEY,
  SUPPORT_ENDED_EVENT,
  type SupportEndedDetail,
  type SupportSessionInfo,
} from './supportSession';

// The largest delay setTimeout honors (2^31 - 1 ms).
const MAX_TIMEOUT_MS = 2_147_483_647;
const RELOAD_NOTICE = "Couldn't finish loading your session in this tab. Reload the page to continue.";

// Backoff for re-hydrating a tab that followed another tab's scope switch.
const REHYDRATE_RETRY_BASE_MS = 2_000;
const REHYDRATE_RETRY_MAX_MS = 30_000;

// How long to wait before retrying an expiry recovery that couldn't reach the server.
const RECOVERY_RETRY_MS = 30_000;

function hasEkomAdminRole(memberships: Membership[]): boolean {
  return memberships.some((m) => m.role === 'ekom_admin');
}

/**
 * The membership the session is actually scoped to: the one whose org matches
 * the session's `organization` (null = the platform membership). The BE returns
 * `memberships` OLDEST FIRST, not active-first, so `memberships[0]` is wrong for
 * anyone holding more than one — e.g. staff inside a client org under a support
 * grant, whose first row is the platform `ekom_admin` membership with no org
 * permissions, which would hide every permission-gated control (SC-2948).
 */
function activeMembership(memberships: Membership[], org: Organization | null): Membership | null {
  const orgId = org?.id ?? null;
  return (
    memberships.find((m) => (m.organization?.id ?? null) === orgId) ?? memberships[0] ?? null
  );
}

/** No staff-scope refresh token to come back with — entering is refused. */
class SupportUnavailableError extends Error {}

/** Fire-and-forget revoke of a refresh token we're done with. */
function revokeRefresh(token: string | null | undefined): void {
  if (token) void authApi.logout(token).catch(() => {});
}

function supportEndedMessage({ organizationName, reason }: SupportEndedDetail): string {
  return reason === 'expired'
    ? `Your support access to ${organizationName} expired. You're back in the EKOM staff view.`
    : `Your support access to ${organizationName} ended (it expired or was revoked). You're back in the EKOM staff view.`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  // Store the full memberships array so `isEkomAdmin` and any future
  // multi-membership consumer (org picker, etc.) see all roles, not just
  // the primary one at index 0.
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(() => !!tokens.getAccess());
  // Staff support session (SC-2948) — see supportSession.ts.
  const [support, setSupport] = useState<SupportSessionInfo | null>(null);
  const [notice, setNotice] = useState<SupportNotice | null>(null);

  // Two hydration paths can run on a single mount: the mount /me *restore* (for a
  // stored token, below) and an *authoritative* login (password / SSO / register).
  // On the /auth/callback load BOTH fire — completeSsoLogin in a child effect, the
  // restore in this provider's effect — so a slow, stale /me could overwrite the
  // account a newer login just established (Greptile P1). `sessionSeq` orders them:
  // an authoritative login bumps it and commits only while still current; the
  // restore *captures without bumping* (it's not authoritative), so it can never
  // supersede a login and its own late response is dropped once a login starts.
  // Whichever request resolves first, the newest authoritative identity wins.
  const sessionSeq = useRef(0);

  const resetState = useCallback(() => {
    setUser(null);
    setOrg(null);
    setMemberships([]);
    setSupport(null);
    // A notice belongs to the session it describes — never show it to the next login.
    setNotice(null);
  }, []);

  // Every identity transition drops client state AND the support marker.
  const resetClientSession = useCallback(() => {
    clearSessionState();
    setSupport(null);
    setNotice(null);
  }, []);

  const routeByRole = useCallback(
    (memberships: Membership[]) => {
      navigate(hasEkomAdminRole(memberships) ? '/admin' : '/');
    },
    [navigate],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const seq = ++sessionSeq.current;
      const data = await authApi.login({ email, password });
      // A newer login/SSO started while this request was in flight — stand down
      // so the winner's session isn't clobbered.
      if (seq !== sessionSeq.current) return;
      // New identity — drop any client state left over from a prior session
      // (query cache + per-catalog stores) before this account's protected
      // views mount and fetch, so it can never render the previous account's
      // cached catalog (SC-2461).
      resetClientSession();
      tokens.setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      setOrg(data.organization);
      setMemberships(data.memberships);
      routeByRole(data.memberships);
    },
    [routeByRole, resetClientSession],
  );

  const completeSsoLogin = useCallback(
    async (accessToken: string, refreshToken: string) => {
      const seq = ++sessionSeq.current;
      // New identity — same fresh-start guarantee as password login (SC-2461).
      resetClientSession();
      tokens.setTokens(accessToken, refreshToken);
      try {
        // SSO delivers only the token pair in the redirect fragment — unlike the
        // password-login JSON, there's no user/org/memberships payload. Hydrate
        // the session via /me before routing (BE handoff, SC-2621).
        const me = await authApi.me();
        // Superseded by a newer login (or the mount restore raced and lost) —
        // don't commit or route over the winner.
        if (seq !== sessionSeq.current) return;
        setUser(me.user);
        setOrg(me.organization);
        setMemberships(me.memberships);
        routeByRole(me.memberships);
      } catch (err) {
        // Superseded — leave the winner's tokens/session intact and swallow.
        if (seq !== sessionSeq.current) return;
        // Never leave a half-session (tokens stored, user null) — that would
        // strand the user on a valid-looking-but-empty session. Drop the tokens
        // and let the callback surface a generic error.
        tokens.clearTokens();
        resetState();
        throw err;
      }
    },
    [routeByRole, resetState, resetClientSession],
  );

  const completeInviteAccept = useCallback(
    (data: AuthResponse) => {
      // The accept POST returns a full AuthResponse (tokens + user/org/
      // memberships), like password login — so, unlike SSO, there's no /me hop.
      // The page has already awaited the POST and owns its error handling; this
      // just commits the resulting session. Bump the session seq so this
      // authoritative identity supersedes any in-flight mount /me restore (an
      // already-authenticated user accepting an attach invite), mirroring the
      // ordering the login/SSO paths use.
      ++sessionSeq.current;
      // New identity — same fresh-start guarantee as login (SC-2461).
      resetClientSession();
      tokens.setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      setOrg(data.organization);
      setMemberships(data.memberships);
      routeByRole(data.memberships);
    },
    [routeByRole, resetClientSession],
  );

  const register = useCallback(
    async (body: RegisterBody) => {
      const seq = ++sessionSeq.current;
      const data = await authApi.register(body);
      if (seq !== sessionSeq.current) return; // superseded by a newer login/SSO
      // Same fresh-start guarantee as login (SC-2461).
      resetClientSession();
      tokens.setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      setOrg(data.organization);
      setMemberships(data.memberships);
      routeByRole(data.memberships);
    },
    [routeByRole, resetClientSession],
  );

  const logout = useCallback(async () => {
    // Optimistic: clear local state and tokens immediately so the UI updates
    // without waiting for the server-side revoke. ProtectedRoute redirects to
    // /login when isAuthenticated flips false. The revoke happens
    // fire-and-forget; if it fails, the local tokens are already gone so the
    // user can't use them anyway.
    // Supersede anything in flight (an org switch, a re-hydrate) so its late
    // response can't sign this session back in.
    ++sessionSeq.current;
    const refresh = tokens.getRefresh();
    // Inside a support session the staff-scope refresh token is still live in
    // the stash — revoke it too, or signing out would leave it usable.
    const stashedPlatformRefresh = supportSession.read()?.platformRefreshToken;
    tokens.clearTokens();
    resetState();
    // Drop all client-side session state (query cache + per-catalog stores +
    // the picked-catalog selection) so the next user on this machine can't see
    // this session's cached data before a refetch (SC-2461).
    clearSessionState();
    revokeRefresh(refresh);
    revokeRefresh(stashedPlatformRefresh);
  }, [resetState]);

  /**
   * Re-scope the session (SC-2948): POST /auth/switch-org, then commit the new
   * token pair as a fresh session — a different org is a different data scope,
   * so it gets the same clean-slate treatment as a login. `grant` = enter that
   * client org under it; null = back to the platform scope. Throws the API error
   * for the caller to word; resolves false when superseded.
   */
  const switchScope = useCallback(
    async (grant: SupportGrant | null): Promise<boolean> => {
      const prev = supportSession.read();
      const currentRefresh = tokens.getRefresh();
      // Entering needs a way back. Without the staff-scope refresh token there'd
      // be no recovery path AND no banner, so refuse before switching rather
      // than land inside a client org unmarked.
      if (grant && !(prev?.platformRefreshToken ?? currentRefresh)) {
        throw new SupportUnavailableError();
      }
      const seq = ++sessionSeq.current;
      const data = await authApi.switchOrg(grant ? grant.organization.id : null);
      if (seq !== sessionSeq.current) {
        // Superseded (e.g. a double click): the pair this call minted is never
        // stored — revoke it rather than leave it live.
        revokeRefresh(data.refresh_token);
        return false;
      }

      resetClientSession();
      tokens.setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      setOrg(data.organization);
      setMemberships(data.memberships);

      if (grant) {
        // The way back is the staff-scope refresh token: the one we held before
        // entering, or — hopping from one client org straight to another — the
        // one stashed when we first left the platform scope.
        const platformRefreshToken = prev?.platformRefreshToken ?? currentRefresh;
        if (platformRefreshToken !== null) {
          const next = {
            organizationId: grant.organization.id,
            organizationName: grant.organization.name,
            role: grant.role,
            expiresAt: grant.expires_at,
            platformRefreshToken,
          };
          supportSession.write(next);
          setSupport(toSupportInfo(next));
        }
        // Org-to-org hop: the previous client org's refresh token is done.
        if (prev) revokeRefresh(currentRefresh);
        navigate('/');
      } else {
        // Exiting: both the client-org refresh token and the stashed staff one
        // are superseded by the fresh platform pair.
        revokeRefresh(currentRefresh);
        revokeRefresh(prev?.platformRefreshToken);
        navigate('/admin');
      }
      // Other tabs share these tokens — tell them the scope moved.
      announceScopeChange();
      return true;
    },
    [navigate, resetClientSession],
  );

  const enterSupport = useCallback(
    async (grant: SupportGrant) => {
      const name = grant.organization.name;
      try {
        await switchScope(grant);
        setNotice(null);
      } catch (err) {
        // 401: the session itself died and the client already handed off to
        // auth:lost / support:ended, which own the messaging.
        if (err instanceof ApiError && err.status === 401) return;
        if (err instanceof SupportUnavailableError) {
          setNotice({
            tone: 'error',
            message: `Couldn't enter ${name}: sign out and back in, then try again.`,
          });
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setNotice({
            tone: 'error',
            message: `Couldn't enter ${name}: the support grant has expired or was revoked.`,
          });
          // The listed grant is stale — refetch so it drops out of the list.
          void queryClient.invalidateQueries({ queryKey: supportGrantKeys.all });
          return;
        }
        setNotice({ tone: 'error', message: `Couldn't enter ${name}. Please try again.` });
      }
    },
    [switchScope],
  );

  const exitSupport = useCallback(async () => {
    const name = support?.organizationName ?? 'the client org';
    try {
      await switchScope(null);
      setNotice(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setNotice({ tone: 'error', message: `Couldn't leave ${name}. Please try again.` });
    }
  }, [switchScope, support]);

  const syncSupportExpiry = useCallback((grant: SupportGrant) => {
    const stash = supportSession.read();
    if (!stash || stash.organizationId !== grant.organization.id) return;
    if (stash.expiresAt === grant.expires_at) return;
    const next = { ...stash, expiresAt: grant.expires_at };
    supportSession.write(next);
    setSupport(toSupportInfo(next));
  }, []);

  const dismissNotice = useCallback(() => setNotice(null), []);

  // Hydrate on mount when an access token is already stored.
  useEffect(() => {
    if (!tokens.getAccess()) return;
    // Capture WITHOUT bumping — the restore is not authoritative, so if a login
    // (e.g. SSO's completeSsoLogin, which runs in a child effect *before* this
    // one) has already bumped the seq, or bumps it while our /me is in flight,
    // our response is stale and must not commit over the login's account.
    const seq = sessionSeq.current;
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.me();
        if (cancelled || seq !== sessionSeq.current) return;
        setUser(me.user);
        setOrg(me.organization);
        setMemberships(me.memberships);
        // A reload inside a client org keeps support mode — but only if the
        // marker is for the org this session is actually scoped to.
        const stash = supportSession.read();
        if (stash && stash.organizationId === me.organization?.id) {
          setSupport(toSupportInfo(stash));
        } else if (stash) {
          supportSession.clear();
        }
      } catch {
        // A login superseded us — leave its tokens alone.
        if (seq !== sessionSeq.current) return;
        // Hydration failed — could be a 401 (client.ts already cleared tokens)
        // OR a non-401 (network error, server down, parse failure). client.ts
        // does NOT clear tokens for non-401 errors, so without this we'd be
        // stranded with valid-looking tokens but null user → ProtectedRoute
        // redirects to /login. Clearing here keeps local state consistent.
        // Phase 2 refactor: split isAuthenticated (tokens exist) from
        // isHydrated (user loaded) so transient errors don't force re-login.
        tokens.clearTokens();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for auth-loss broadcast from the API client. State reset is enough —
  // ProtectedRoute will redirect to /login when isAuthenticated flips false.
  // Doing an explicit navigate here too caused a brief double-redirect flash.
  useEffect(() => {
    const handler = () => {
      ++sessionSeq.current;
      resetState();
      // The session is gone — clear its cached data too, mirroring logout, so a
      // subsequent login as a different account starts clean (SC-2461).
      clearSessionState();
      setIsLoading(false);
    };
    window.addEventListener('auth:lost', handler);
    return () => window.removeEventListener('auth:lost', handler);
  }, [resetState]);

  // The support grant died under us and the API client has already swapped in a
  // platform-scope token pair (SC-2948). Leave the client org immediately —
  // clear its data, drop the org so nothing renders against it, land on the
  // staff page — and re-hydrate memberships for the new scope.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<SupportEndedDetail>).detail;
      const seq = ++sessionSeq.current;
      resetClientSession();
      setOrg(null);
      setNotice({ tone: 'info', message: supportEndedMessage(detail) });
      navigate('/admin');
      void authApi
        .me()
        .then((me) => {
          if (seq !== sessionSeq.current) return;
          setUser(me.user);
          setOrg(me.organization);
          setMemberships(me.memberships);
        })
        .catch((err: unknown) => {
          if (seq !== sessionSeq.current) return;
          // A rejected session was already handled by the API client (refresh,
          // then auth:lost). Anything else is transient — and the tokens are
          // shared by every tab, so clearing them here would sign them ALL out
          // over one failed read. Keep the session; a reload re-hydrates it.
          if (err instanceof ApiError && err.status === 401) return;
          setNotice({ tone: 'error', message: RELOAD_NOTICE });
        });
    };
    window.addEventListener(SUPPORT_ENDED_EVENT, handler);
    return () => window.removeEventListener(SUPPORT_ENDED_EVENT, handler);
  }, [navigate, resetClientSession]);

  // Leave the client org the moment the grant expires, rather than waiting for
  // the next request to fail. Expiry is enforced server-side at request time,
  // so this is courtesy, not the boundary.
  const supportExpiresAt = support?.expiresAt;
  useEffect(() => {
    if (!supportExpiresAt) return;
    let id: number | undefined;
    const arm = () => {
      const ms = Date.parse(supportExpiresAt) - Date.now();
      if (ms > 0) {
        // setTimeout overflows past ~24.8 days and fires at once — wait in
        // capped steps and re-check.
        id = window.setTimeout(arm, Math.min(ms, MAX_TIMEOUT_MS));
        return;
      }
      leave();
    };
    const leave = () => {
      // Already exited (or recovered) by another path — nothing to do.
      if (!supportSession.read()) return;
      void recoverPlatformSession('expired').then((outcome) => {
        if (cancelled) return;
        if (outcome === 'retry') {
          // Offline or the server is struggling: the staff session is fine,
          // so keep trying rather than sign out.
          id = window.setTimeout(leave, RECOVERY_RETRY_MS);
        } else if (outcome === 'unrecoverable') {
          // The staff-scope token is dead too: a genuine sign-out.
          tokens.clearTokens();
          window.dispatchEvent(new Event('auth:lost'));
        }
        // 'recovered' → the support:ended handler takes over; 'superseded' →
        // a newer session owns the tab.
      });
    };
    let cancelled = false;
    arm();
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [supportExpiresAt]);

  // Another tab changed the org scope (entered, hopped, exited, or recovered).
  // The tokens this tab will send next already belong to that scope, so drop
  // everything rendered for the old one and re-hydrate. Keep the support marker:
  // the other tab just wrote it for the session we're adopting.
  //
  // Until /me answers, the tab stays behind the full-screen loading state
  // (`isLoading`): the shared tokens may already be a client org's, and its
  // data must never render without the support banner, Exit and the expiry
  // timer. A transient failure retries rather than unblocking the tab.
  useEffect(() => {
    let retryId: number | undefined;
    const hydrate = (seq: number, attempt: number) => {
      void authApi
        .me()
        .then((me) => {
          if (seq !== sessionSeq.current) return;
          setUser(me.user);
          setOrg(me.organization);
          setMemberships(me.memberships);
          const live = supportSession.read();
          setSupport(live && live.organizationId === me.organization?.id ? toSupportInfo(live) : null);
          setNotice({
            tone: 'info',
            message: me.organization
              ? `This tab moved to ${me.organization.name} because you switched in another tab.`
              : "This tab moved back to the EKOM staff view because you switched in another tab.",
          });
          setIsLoading(false);
        })
        .catch((err: unknown) => {
          if (seq !== sessionSeq.current) return;
          // A rejected session was already handled by the API client (refresh,
          // then auth:lost, which unblocks to /login). Anything else is
          // transient — and the tokens are shared by every tab, so clearing them
          // here would sign them ALL out over one failed read. Keep the session
          // and try again.
          if (err instanceof ApiError && err.status === 401) return;
          const delay = Math.min(REHYDRATE_RETRY_BASE_MS * 2 ** attempt, REHYDRATE_RETRY_MAX_MS);
          retryId = window.setTimeout(() => hydrate(seq, attempt + 1), delay);
        });
    };
    const handler = (e: StorageEvent) => {
      if (e.key !== SCOPE_CHANGED_KEY) return;
      const seq = ++sessionSeq.current;
      window.clearTimeout(retryId);
      clearSessionState({ keepSupportSession: true });
      setSupport(null);
      setOrg(null);
      setIsLoading(true);
      navigate(supportSession.read() ? '/' : '/admin');
      hydrate(seq, 0);
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.clearTimeout(retryId);
    };
  }, [navigate]);

  const activeSupport = support && support.organizationId === org?.id ? support : null;

  return (
    <AuthContext.Provider
      value={{
        user,
        org,
        // The membership the session is scoped to — matched on org, never
        // positional (see `activeMembership`).
        membership: activeMembership(memberships, org),
        isAuthenticated: !!user,
        isEkomAdmin: hasEkomAdminRole(memberships),
        isLoading,
        login,
        completeSsoLogin,
        completeInviteAccept,
        register,
        logout,
      }}
    >
      <SupportContext.Provider
        value={{
          support: activeSupport,
          notice,
          dismissNotice,
          enterSupport,
          exitSupport,
          syncSupportExpiry,
        }}
      >
        {children}
      </SupportContext.Provider>
    </AuthContext.Provider>
  );
}
